import React from 'react';
import { Box, Typography, Grid, Container, useTheme, alpha, Card } from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
import { StyledTextField, StyledSelect, StyledDatePicker } from './index';
import { MenuItem } from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

const employeeRanges = [
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
];

const CompanyInformation = ({ 
  companyInfo, 
  handleInputChange, 
  loading, 
  requiredFields, 
  validationErrors 
}) => {
  const { hasPermission, canAccessPage, canEditPage } = usePermissions();
  const theme = useTheme();
  
  // Permission check for company information access
  if (!canAccessPage('company_information')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 6,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 500,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Company Information Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              You don't currently have the necessary permissions to access the Company Information settings. Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }
  
  const isFieldRequired = (fieldName) => {
    return requiredFields['company-info']?.includes(fieldName) || false;
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const canEditSettings = canEditPage('company_information');

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Company Information
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            maxWidth: '400px'
          }}>
            Essential business details and organizational information for your company
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledTextField 
            label="Company Name" 
            name="name"
            value={companyInfo.name} 
            onChange={handleInputChange} 
            required={isFieldRequired('name')}
            error={!!getFieldError('name')}
            helperText={getFieldError('name')}
            disabled={loading || !canEditSettings}
            placeholder="Enter your company name"
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField 
            label="Legal Name" 
            name="legal_name"
            value={companyInfo.legal_name} 
            onChange={handleInputChange} 
            required={isFieldRequired('legal_name')}
            error={!!getFieldError('legal_name')}
            helperText={getFieldError('legal_name')}
            disabled={loading || !canEditSettings}
            placeholder="Legal business name"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField
            label="Industry"
            name="industry"
            value={companyInfo.industry}
            onChange={handleInputChange}
            required={isFieldRequired('industry')}
            error={!!getFieldError('industry')}
            helperText={getFieldError('industry')}
            disabled={loading || !canEditSettings}
            placeholder="e.g. Technology, Healthcare, Finance"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField
            label="Business Type"
            name="business_type"
            value={companyInfo.business_type}
            onChange={handleInputChange}
            required={isFieldRequired('business_type')}
            error={!!getFieldError('business_type')}
            helperText={getFieldError('business_type')}
            disabled={loading || !canEditSettings}
            placeholder="e.g. LLC, Corporation, Partnership"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledDatePicker
            label="Founded Date"
            value={companyInfo.founded_date ? dayjs(companyInfo.founded_date) : null}
            onChange={(newValue) => {
              handleInputChange({
                target: {
                  name: 'founded_date',
                  value: newValue ? newValue.format('YYYY-MM-DD') : ''
                }
              });
            }}
            disabled={loading || !canEditSettings}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledSelect
            label="Employee Count"
            name="employee_count"
            value={companyInfo.employee_count}
            onChange={handleInputChange}
            disabled={loading || !canEditSettings}
          >
            {employeeRanges.map((range) => (
              <MenuItem key={range} value={range}>{range} employees</MenuItem>
            ))}
          </StyledSelect>
        </Grid>
        <Grid item xs={12}>
          <StyledTextField 
            label="Business License Number" 
            name="business_license"
            value={companyInfo.business_license} 
            onChange={handleInputChange} 
            disabled={loading || !canEditSettings}
            placeholder="Business license number"
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField 
            label="Description" 
            name="description"
            value={companyInfo.description} 
            onChange={handleInputChange} 
            disabled={loading || !canEditSettings}
            placeholder="Brief description of your business..."
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyInformation;